import type { SWRConfiguration } from 'swr'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'

export interface EventBasicConfig {
  enabled: boolean
  startDate: string
  endDate: string
  enabledForAdmin?: boolean
}

/**
 * Config types of events.
 * Should be extended via module augmentation.
 *
 * @see packages/pixai-app/src/features/GrowthEvent/ExampleEvent/index.tsx
 */
export interface DynamicGrowthEvents {
  christmasEvent2025?: EventBasicConfig & {
    dramaBanner?: {
      media?: {
        zh?: string
        en?: string
        ja?: string
        ko?: string
      }
      targetUrl?: string
    }
    homeBanner?: {
      media?: {
        zh?: string
        en?: string
        ja?: string
        ko?: string
      }
      targetUrl?: string
    }
    xProfileBanner?: {
      targetUrl?: string
    }
    faqLink?: {
      targetUrls?: {
        zh?: string
        en?: string
        ja?: string
      }
    }
    promotions?: {
      itemType?: string
      itemId?: string
      reward?: {
        source?: 'early_bird'
        decorationCodes?: string[]
        extraPackageBoostPercentage?: number
        startDate?: string
        endDate?: string
      }
    }[]
    countdown?: {
      enabled?: boolean
      startDate?: string
      endDate?: string
    }
    earlybirdEndDate?: string
    countdownForEarlybird?: {
      enabled?: boolean
      startDate?: string
    }
  }
  newYearEvent2026?: EventBasicConfig & {
    homeBanner?: {
      media?: {
        zh?: string
        en?: string
        ja?: string
        ko?: string
      }
      targetUrl?: string
    }
    promotions?: {
      itemType?: string
      itemId?: string
      reward?: {
        decorationCodes?: string[]
        extraPackageBoostPercentage?: number
      }
    }[]
    countDown?: {
      enabled?: boolean
      startDate?: string
    }
    countdown?: {
      enabled?: boolean
      startDate?: string
      endDate?: string
    }
  }
  valentineEvent2026?: EventBasicConfig & {
    homeBanner?: {
      media?: {
        zh?: string
        en?: string
        ja?: string
        ko?: string
      }
      targetUrl?: string
    }
    countdown?: {
      enabled?: boolean
      startDate?: string
      endDate?: string
    }
    earlybirdEndDate?: string
    countdownForEarlybird?: {
      enabled?: boolean
      startDate?: string
    }
    imageEditParameters?: {
      modelId?: string
      modelConfig?: {
        resolution?: string
      }
    }
    t2i2vParameters?: {
      model?: string
      mode?: string
      duration?: number
      mediaId?: string
      generateAudio?: boolean
    }
    prompts?: Array<{
      key: string
      imageEditPrompt: string
      t2i2vPrompt: string
    }>
    presetImages?: Array<string>
    promotions?: {
      itemType?: string
      itemId?: string
      reward?: {
        source?: 'early_bird'
        decorationCodes?: string[]
        extraPackageBoostPercentage?: number
        startDate?: string
        endDate?: string
      }
    }[]
  }
  springPromotionEvent2026?: EventBasicConfig & {
    homeBanner?: {
      media?: {
        zh?: string
        en?: string
        ja?: string
        ko?: string
      }
      targetUrl?: string
    }
    countdown?: {
      enabled?: boolean
      startDate?: string
      endDate?: string
    }
    earlybirdEndDate?: string
    countdownForEarlybird?: {
      enabled?: boolean
      startDate?: string
    }
    promotions?: {
      itemType?: string
      itemId?: string
      reward?: {
        source?: 'early_bird'
        decorationCodes?: string[]
        extraPackageBoostPercentage?: number
        startDate?: string
        endDate?: string
      }
    }[]
    taskRewardConfig?: {
      totalCap?: number
      milestones?: {
        threshold: number
        rewardType: 'coupon' | 'decoration' | 'credits'
        rewardValue?: string | number
      }[]
    }
  }
}

export const useGrowthEventDynamicConfig = (opt?: SWRConfiguration) => {
  return usePublicDynamicConfig<DynamicGrowthEvents>('growthEvents', opt)
}
